import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import LiveCaptureModal from './LiveCaptureModal';
import './PhotoUpload.css';


// Mobile-Friendly Swipeable Example Carousel with Dot Indicators
const ExampleCarousel = ({ photos, categoryLabel, onSelectImage }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  if (!photos || photos.length === 0) {
    return (
      <div className="example-empty-box">
        <span>No example images available for {categoryLabel}.</span>
      </div>
    );
  }

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const idx = Math.round(scrollLeft / Math.max(1, clientWidth * 0.75));
      setActiveIndex(Math.min(photos.length - 1, Math.max(0, idx)));
    }
  };

  const scrollToSlide = (idx) => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.clientWidth * 0.75;
      scrollRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
      setActiveIndex(idx);
    }
  };

  return (
    <div className="mobile-example-carousel">
      <div className="example-carousel-track" ref={scrollRef} onScroll={handleScroll}>
        {photos.map((photo, i) => {
          const imgUrl = `${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${photo.id}/file`;
          return (
            <div
              key={photo.id || i}
              className="example-carousel-card"
              onClick={() => onSelectImage(imgUrl)}
              title="Tap to preview"
            >
              <img
                src={imgUrl}
                alt={`Example for ${photo.category || categoryLabel}`}
                className="example-carousel-img"
              />
              <div className="example-carousel-tag">
                {(photo.category || categoryLabel).replace('present_', '')}
              </div>
            </div>
          );
        })}
      </div>
      {photos.length > 1 && (
        <div className="example-carousel-dots">
          {photos.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`carousel-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => scrollToSlide(i)}
              aria-label={`View example ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const PhotoUpload = () => {
  const { userName } = useParams();
  const navigate = useNavigate();

  const [actualUserId, setActualUserId] = useState(sessionStorage.getItem('userId') || null);
  // eslint-disable-next-line no-unused-vars
  const [userGender, setUserGender] = useState('');
  const [currentStep, setCurrentStep] = useState('old'); // 'present', 'poses', 'old', 'hand_in'
  const [activePoseSubtab, setActivePoseSubtab] = useState('a'); // 'a' or 'b'
  const [oldPhotos, setOldPhotos] = useState([]);
  const [presentPhotos, setPresentPhotos] = useState([]);
  const [postAPhotos, setPostAPhotos] = useState([]);
  const [postBPhotos, setPostBPhotos] = useState([]);
  const [presentUploadMethod, setPresentUploadMethod] = useState('files'); // 'files' or 'camera'
  const [postAUploadMethod, setPostAUploadMethod] = useState('files');
  const [postBUploadMethod, setPostBUploadMethod] = useState('files');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [examplePhotos, setExamplePhotos] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [displayName, setDisplayName] = useState(userName);
  const [duplicateDateWarning, setDuplicateDateWarning] = useState('');
  const [showWhyModal, setShowWhyModal] = useState(false);

  // Legacy camera states removed in favor of LiveCaptureModal

  // Refs for setInterval closures
  const currentStepRef = useRef(currentStep);
  const postAPhotosRef = useRef(postAPhotos);
  const postBPhotosRef = useRef(postBPhotos);

  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => { postAPhotosRef.current = postAPhotos; }, [postAPhotos]);
  useEffect(() => { postBPhotosRef.current = postBPhotos; }, [postBPhotos]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e, uploadHandler) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const fakeEvent = { target: { files: e.dataTransfer.files } };
      uploadHandler(fakeEvent);
    }
  };

  // Fetch existing user photos on load
  const loadUserPhotos = useCallback(async () => {
    if (!userName || userName === 'undefined') {
      navigate('/login');
      return;
    }
    
    // Auth Check
    const sessionUserName = sessionStorage.getItem('userName');
    const isAdminToken = sessionStorage.getItem('adminToken');
    if (!isAdminToken && sessionUserName && sessionUserName !== userName) {
      navigate(`/upload/${encodeURIComponent(sessionUserName)}`);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.getUserStatus(userName);
      if (data) {
        if (data.user?.id) setActualUserId(data.user.id);
        if (data.user?.name) setDisplayName(data.user.name);
        if (data.user?.gender) setUserGender(data.user.gender);
        if (data.old_photo_status?.photos) {
          setOldPhotos(data.old_photo_status.photos);
        }
        if (data.present_photos_status?.photos) {
          setPresentPhotos(data.present_photos_status.photos);
        }
        if (data.post_a_status?.photos) {
          setPostAPhotos(data.post_a_status.photos);
        }
        if (data.post_b_status?.photos) {
          setPostBPhotos(data.post_b_status.photos);
        }
      }
    } catch (err) {
      console.warn('Could not load existing photos:', err);
    } finally {
      setLoading(false);
    }
  }, [userName, navigate]);

  useEffect(() => {
    loadUserPhotos();
    apiService.getExamplePhotos().then(data => {
      setExamplePhotos(data.photos || []);
    }).catch(err => console.error('Failed to fetch example photos:', err));

    
  }, [loadUserPhotos]);

  const validateFileSize = (file) => {
    const maxSize = 16 * 1024 * 1024; // 16MB
    const minSize = 128 * 1024; // 128KB for all photos

    if (file.size > maxSize) {
      setError(`"${file.name}" is too large. Maximum 16MB allowed.`);
      return false;
    }

    if (file.size < minSize) {
      setError(`"${file.name}" is too small (${(file.size / 1024).toFixed(0)}KB). Minimum 128KB required for all photos.`);
      return false;
    }

    return true;
  };

  // Upload old photos (multiple allowed)
  const handleOldPhotosUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    setDuplicateDateWarning(''); // Clear previous warning

    for (const file of files) {
      if (!validateFileSize(file)) continue;

      try {
        setLoading(true);
        const response = await apiService.uploadOldPhoto(actualUserId, [file]);
        if (response.photos) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setOldPhotos(prev => [...prev, ...photosWithPreview]);

          // Check if any uploaded photo was rejected due to duplicate date
          const allValidations = response.all_validations || (response.validation_results ? [response.validation_results] : []);
          for (const vr of allValidations) {
            const reasons = vr.rejection_reasons || [];
            const dupReason = reasons.find(r => r.toLowerCase().includes('duplicate date'));
            if (dupReason) {
              setDuplicateDateWarning(dupReason);
              break;
            }
          }
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    e.target.value = '';
  };

  // Upload present photos
  const uploadPresentPhotos = useCallback(async (files) => {
    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPresentPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPresentPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePresentFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file)) {
        return;
      }
    }

    await uploadPresentPhotos(files);
    e.target.value = '';
  };

  const uploadPostAPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostAPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostAPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePostAFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file)) {
        return;
      }
    }

    await uploadPostAPhotos(files);
    e.target.value = '';
  };

  const uploadPostBPhotos = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    for (const file of files) {
      try {
        setLoading(true);
        const response = await apiService.uploadPostBPhotos(actualUserId, [file]);
        if (response.photos && response.photos.length > 0) {
          const previewUrl = URL.createObjectURL(file);
          const photosWithPreview = response.photos.map(p => ({
            ...p,
            preview_url: previewUrl
          }));
          setPostBPhotos(prev => [...prev, ...photosWithPreview]);
        } else {
          await loadUserPhotos();
        }
      } catch (err) {
        console.error('Upload error:', err);
        setError(err.response?.data?.error || 'Upload failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  }, [actualUserId, loadUserPhotos]);

  const handlePostBFilesUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files || files.length === 0) return;

    for (const file of files) {
      if (!validateFileSize(file)) {
        return;
      }
    }

    await uploadPostBPhotos(files);
    e.target.value = '';
  };

  // Legacy video capture routines migrated to LiveCaptureModal

  // Delete a specific photo
  const handleDeletePhoto = async (photoId, type) => {
    if (!window.confirm('Are you sure you want to remove this image?')) return;
    try {
      setLoading(true);
      await apiService.deletePhoto(photoId);
      if (type === 'old') {
        setOldPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'present') {
        setPresentPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_a') {
        setPostAPhotos(prev => prev.filter(p => p.id !== photoId));
      } else if (type === 'post_b') {
        setPostBPhotos(prev => prev.filter(p => p.id !== photoId));
      }
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to remove photo.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAllPhotos = async (type) => {
    let photosToRemove = [];
    if (type === 'old') photosToRemove = oldPhotos;
    else if (type === 'present') photosToRemove = presentPhotos;
    else if (type === 'post_a') photosToRemove = postAPhotos;
    else if (type === 'post_b') photosToRemove = postBPhotos;

    if (photosToRemove.length === 0) return;
    if (!window.confirm(`Are you sure you want to remove ALL ${photosToRemove.length} uploaded photos in this section?`)) return;

    try {
      setLoading(true);
      // Delete concurrently for speed
      await Promise.all(photosToRemove.map(p => apiService.deletePhoto(p.id)));

      if (type === 'old') setOldPhotos([]);
      else if (type === 'present') setPresentPhotos([]);
      else if (type === 'post_a') setPostAPhotos([]);
      else if (type === 'post_b') setPostBPhotos([]);

    } catch (err) {
      console.error('Error deleting all photos:', err);
      setError('Failed to remove all photos. Some may have been left behind.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleOverridePose = async (photoId, poseToAssign) => {
    if (!poseToAssign) return;
    try {
      setLoading(true);
      await apiService.overridePhotoPose(photoId, poseToAssign);

      // Update local state to reflect the new pose
      const updateFn = prev => prev.map(p => {
        if (p.id === photoId) {
          const newValidation = { ...(p.validation_results || {}) };
          newValidation.is_valid = true;
          newValidation.detected_pose = poseToAssign;
          return { ...p, is_valid: true, validation_results: newValidation };
        }
        return p;
      });

      setPostAPhotos(updateFn);
      setPostBPhotos(updateFn);
    } catch (err) {
      console.error('Error overriding pose:', err);
      setError('Failed to override photo pose.');
    } finally {
      setLoading(false);
    }
  };

    const validOldPhotos = oldPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  const validPresentPhotos = presentPhotos.filter(p => p.is_valid || p.validation_results?.is_valid);
  
  // Progress calculations: 5 selfies + 8 poses = 13 items total
  const completedSelfies = Math.min(5, validPresentPhotos.length);
  const completedPoseA = Math.min(4, postAPhotos.length);
  const completedPoseB = Math.min(4, postBPhotos.length);
  const globalCompletedCount = completedSelfies + completedPoseA + completedPoseB;
  const globalProgressPercent = Math.min(100, Math.round((globalCompletedCount / 13) * 100));

  const isSelfiesSatisfied = validPresentPhotos.length >= 2;
  const isPosesSatisfied = (postAPhotos.length >= 2 && postBPhotos.length >= 2) || (postAPhotos.length + postBPhotos.length >= 4);
  const isHistorySatisfied = validOldPhotos.length >= 3;
  const isReadyForHandIn = isSelfiesSatisfied && isPosesSatisfied && isHistorySatisfied;

  const canAccessPresent = oldPhotos.length >= 3;
  const canAccessPost = validPresentPhotos.length >= 2;

  const goToDashboard = () => {
    navigate(`/dashboard/${encodeURIComponent(userName)}`);
  };

  return (
    <div className="photo-upload-container">
      {/* Mobile-First Header & Decluttered Info */}
      <div className="mobile-view-header">
        <div className="mobile-header-text">
          <h2 className="mobile-step-title">
            Imaginary <span className="title-user-name">— {displayName}</span>
          </h2>
          <p className="mobile-step-subtitle">
            {currentStep === 'old' && 'Step 1: Upload 3–5 historical photos from your gallery.'}
            {currentStep === 'present' && 'Step 2: Capture 5 live selfies with different backgrounds.'}
            {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && 'Step 3: Capture directional & diagonal poses.'}
            {currentStep === 'hand_in' && 'Step 4: Final verification review & submission.'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowWhyModal(true)}
          className="why-photos-link-btn"
          title="Learn why these photos are needed"
        >
          <span>Why do we need these photos?</span>
          <span className="why-info-badge">ⓘ</span>
        </button>
      </div>

      {/* Why Do We Need Photos Collapsible Modal */}
      {showWhyModal && (
        <div className="modal-backdrop-overlay" onClick={() => setShowWhyModal(false)}>
          <div className="modal-dialog-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header-row">
              <span className="modal-shield-badge">🔒 TELUS VERIFICATION STANDARD</span>
              <button
                type="button"
                className="modal-close-icon-btn"
                onClick={() => setShowWhyModal(false)}
                aria-label="Close"
              >✕</button>
            </div>
            <h3 className="modal-headline">Why Authentic Photos Matter</h3>
            <div className="modal-body-scroll">
              <p className="modal-lead-para">
                By providing your authentic longitudinal photo history alongside live captures, you allow generative models to learn your <strong>real facial aging trajectories</strong>.
              </p>
              <div className="modal-benefits-list">
                <div className="benefit-item">
                  <span className="benefit-icon">🧬</span>
                  <div className="benefit-text">
                    <strong>Authentic Aging Models</strong>
                    <span>Enables realistic, personalized digital avatars rather than generic synthetic filters.</span>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🛡️</span>
                  <div className="benefit-text">
                    <strong>Forensic Anti-Spoofing</strong>
                    <span>Validates physical identity, real-world camera EXIF, and liveness compliance.</span>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🔐</span>
                  <div className="benefit-text">
                    <strong>Enterprise Privacy & Security</strong>
                    <span>All biometric data is encrypted and handled under strict client data confidentiality protocols.</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer-row">
              <button
                type="button"
                className="modal-confirm-btn"
                onClick={() => setShowWhyModal(false)}
              >
                Understood & Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Horizontal Progress Stepper: (1) Historical -> (2) Selfies -> (3) Poses -> (4) Review */}
      <div className="mobile-horizontal-stepper">
        <div className="stepper-connecting-bar">
          <div
            className="stepper-connecting-fill"
            style={{
              width: currentStep === 'old' ? '12%' : currentStep === 'present' ? '38%' : (currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') ? '68%' : '100%'
            }}
          />
        </div>
        <div className="stepper-nodes-row">
          {/* Node 1: Historical */}
          <button
            type="button"
            className={`stepper-node-btn ${currentStep === 'old' ? 'active' : ''} ${isHistorySatisfied ? 'completed' : ''}`}
            onClick={() => setCurrentStep('old')}
          >
            <div className="stepper-node-circle">
              {isHistorySatisfied ? '✓' : '1'}
            </div>
            <span className="stepper-node-name">Historical</span>
            <span className="stepper-node-count">{validOldPhotos.length}/3</span>
          </button>

          {/* Node 2: Selfies */}
          <button
            type="button"
            className={`stepper-node-btn ${currentStep === 'present' ? 'active' : ''} ${isSelfiesSatisfied ? 'completed' : ''}`}
            onClick={() => setCurrentStep('present')}
          >
            <div className="stepper-node-circle">
              {isSelfiesSatisfied ? '✓' : '2'}
            </div>
            <span className="stepper-node-name">Selfies</span>
            <span className="stepper-node-count">{validPresentPhotos.length}/5</span>
          </button>

          {/* Node 3: Poses */}
          <button
            type="button"
            className={`stepper-node-btn ${(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') ? 'active' : ''} ${isPosesSatisfied ? 'completed' : ''}`}
            onClick={() => setCurrentStep('poses')}
          >
            <div className="stepper-node-circle">
              {isPosesSatisfied ? '✓' : '3'}
            </div>
            <span className="stepper-node-name">Poses</span>
            <span className="stepper-node-count">{postAPhotos.length + postBPhotos.length}/8</span>
          </button>

          {/* Node 4: Review */}
          <button
            type="button"
            className={`stepper-node-btn ${currentStep === 'hand_in' ? 'active' : ''} ${isReadyForHandIn ? 'completed' : ''}`}
            onClick={() => setCurrentStep('hand_in')}
          >
            <div className="stepper-node-circle">
              {isReadyForHandIn ? '✓' : '4'}
            </div>
            <span className="stepper-node-name">Review</span>
            <span className="stepper-node-count">{isReadyForHandIn ? 'Ready' : 'Pending'}</span>
          </button>
        </div>
      </div>

      {/* STEP 1: OLD PHOTOS */}
      {currentStep === 'old' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 1: Old Photos Review & Approval</h3>
            <p>Upload your past photo(s). Review automatic validation checks before approving.</p>
          </div>

          {/* Scannable Mobile Criteria Card */}
          <div className="mobile-criteria-card">
            <div className="criteria-header-row">
              <span className="criteria-header-title">📋 Photo Requirements</span>
              <span className="criteria-header-badge">3 Photos Minimum</span>
            </div>
            <div className="criteria-items-grid">
              <div className="criteria-item-row">
                <span className="criteria-item-icon">📁</span>
                <div className="criteria-item-details">
                  <strong>File Size:</strong> Min 128 KB (Original quality only)
                </div>
              </div>
              <div className="criteria-item-row">
                <span className="criteria-item-icon">👁️</span>
                <div className="criteria-item-details">
                  <strong>Face:</strong> Clear, single face with eyes open
                </div>
              </div>
              <div className="criteria-item-row">
                <span className="criteria-item-icon">📱</span>
                <div className="criteria-item-details">
                  <strong>Source:</strong> Original camera photo (no screenshots or web downloads)
                </div>
              </div>
            </div>
            <div className="criteria-desktop-hint">
              💡 Tip: Prefer uploading from a computer? You can open this link in any desktop browser.
            </div>
          </div>

          {/* Swipeable Example Carousel */}
          <div className="mobile-examples-container">
            <div className="examples-header-label">Examples of acceptable historical photos:</div>
            <ExampleCarousel
              photos={examplePhotos.filter(p => {
                const cat = (p.category || '').toLowerCase();
                return ['female', 'girl', 'male'].includes(cat);
              })}
              categoryLabel="historical"
              onSelectImage={setSelectedImage}
            />
          </div>

          {/* Mobile-Friendly Gallery Upload Area */}
          <div className="file-upload">
            <label
              className={`file-upload-label mobile-upload-dropzone ${isDragging ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, handleOldPhotosUpload)}
            >
              <div className="upload-camera-badge">📸</div>
              <span className="upload-primary-text">Tap to select photos from your gallery</span>
              <span className="upload-subtext">Original camera photos & HEIC supported • Min 3 photos</span>
              <input
                type="file"
                multiple
                accept="image/*,.heic,.heif"
                onChange={handleOldPhotosUpload}
                disabled={loading}
                className="file-input"
              />
            </label>
          </div>

          {/* Uploaded Old Photos Inspection Grid */}
          {duplicateDateWarning && (
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              border: '2px solid #f59e0b',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              boxShadow: '0 2px 8px rgba(245,158,11,0.25)'
            }}>
              <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.25rem', fontSize: '1rem' }}>
                  Duplicate Date Detected!
                </strong>
                <p style={{ color: '#78350f', margin: 0, fontSize: '0.9rem' }}>{duplicateDateWarning}</p>
                <p style={{ color: '#78350f', margin: '0.35rem 0 0', fontSize: '0.85rem' }}>
                  Each historical photo must have a unique capture date. Please upload a photo taken on a different date.
                </p>
              </div>
              <button
                onClick={() => setDuplicateDateWarning('')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#92400e', lineHeight: 1, paddingTop: '0.1rem' }}
                title="Dismiss"
              >✕</button>
            </div>
          )}

          {oldPhotos.length > 0 && (
            <div className="photo-inspection-section">
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <h4>Uploaded Old Photos ({oldPhotos.length})</h4>
                  <span className="badge-summary">
                    {validOldPhotos.length} Accepted • {oldPhotos.length - validOldPhotos.length} Rejected
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveAllPhotos('old')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {(() => {
                  // Detect duplicate EXIF dates across historical photos
                  const dateCounts = {};
                  oldPhotos.forEach(p => {
                    const d = (p.validation_results?.exif_date || '').split(' ')[0];
                    if (d) dateCounts[d] = (dateCounts[d] || 0) + 1;
                  });
                  const duplicateDates = new Set(Object.keys(dateCounts).filter(d => dateCounts[d] > 1));

                  return oldPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const reasons = val.rejection_reasons || [];
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Old upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='14'%3EPhoto Uploaded%3C/text%3E%3C/svg%3E"; }}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Historical {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'old')}
                            className="delete-photo-btn"
                            title="Delete"
                          >
                            🗑️ Remove
                          </button>
                        </div>

                        {/* Checks list */}
                        <div className="checks-list">
                          <div className="check-row">
                            <span>File Size (&ge; 128KB):</span>
                            <span className={val.file_size_valid ? 'text-ok' : 'text-bad'}>
                              {val.file_size_valid ? '✓ Passed' : '✗ Too small'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Subjects Detected:</span>
                            <span className={val.subjects_detected > 0 ? 'text-ok' : 'text-bad'}>
                              {val.subjects_detected > 0 ? `✓ ${val.subjects_detected} found` : '✗ None'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Eyes Visible:</span>
                            <span className={val.eyes_visible ? 'text-ok' : 'text-bad'}>
                              {val.eyes_visible ? '✓ Visible' : '✗ Obscured'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>

                        {/* Rejection Reasons Box */}
                        {!isValid && (
                          <div className="rejection-box">
                            <span className="rejection-title">⚠️ Rejection Reason:</span>
                            {reasons.length > 0 ? (
                              <ul className="reasons-list">
                                {reasons.map((r, i) => <li key={i}>{r}</li>)}
                              </ul>
                            ) : (
                              <p className="single-reason">Does not meet the photo quality criteria.</p>
                            )}
                          </div>
                        )}

                        {/* Duplicate Date Warning */}
                        {(() => {
                          const dateOnly = (val.exif_date || '').split(' ')[0];
                          return dateOnly && duplicateDates.has(dateOnly) ? (
                            <div style={{
                              marginTop: '0.75rem',
                              padding: '0.65rem 0.85rem',
                              background: '#fff7ed',
                              border: '1.5px solid #f97316',
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.5rem'
                            }}>
                              <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>⚠️</span>
                              <span style={{ fontSize: '0.82rem', color: '#9a3412', lineHeight: 1.45 }}>
                                <strong>Duplicate date detected.</strong> Another photo already has the same date (<strong>{dateOnly}</strong>). Each historical photo must be taken on a different day — please remove this one and upload a photo from a different date.
                              </span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  );
                  })
                })()}
              </div>

              {/* Pre-Approval Action Bar */}
              <div className="approval-action-bar">
                {canAccessPresent ? (
                  <div className="approval-box approved-state">
                    <div className="approval-text">
                      <strong>✅ 16 Photos Uploaded!</strong>
                      <p>You have uploaded {oldPhotos.length} historical photo(s) ({validOldPhotos.length} accepted). You may proceed to the next step.</p>
                    </div>
                    <button
                      onClick={() => setCurrentStep('present')}
                      className="approve-proceed-btn"
                    >
                      Next
                    </button>
                  </div>
                ) : (
                  <div className="approval-box rejected-state">
                    <div className="approval-text">
                      <strong>⚠️ {16 - oldPhotos.length} More Photo(s) Required</strong>
                      <p>You have uploaded {oldPhotos.length} of 16 required historical photos. Please upload at least 16 photos to proceed — not all need to be accepted.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="step-navigation">
            <div></div>
            <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('present')} disabled={!canAccessPresent}
              title={!canAccessPresent ? `Upload ${16 - oldPhotos.length} more photo(s) to proceed` : 'Proceed to Present Neutral'}
            >
              Next Step &#8594;
            </button>
          </div>
        </div>
      )}

      {/* STEP 2: PRESENT PHOTOS */}
      {currentStep === 'present' && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 2: Live Selfie Captures (5 Target, 2 Min)</h3>
            <p>Capture front-facing selfies with different backgrounds.</p>
          </div>

          {/* Scannable Criteria Card for Selfies */}
          <div className="mobile-criteria-card">
            <div className="criteria-header-row">
              <span className="criteria-header-title">🤳 Selfie Criteria</span>
              <span className="criteria-header-badge">Target: 5 Selfies</span>
            </div>
            <div className="criteria-items-grid">
              <div className="criteria-item-row">
                <span className="criteria-item-icon">📸</span>
                <div className="criteria-item-details">
                  <strong>Pose & Frame:</strong> Look straight at camera, face centered
                </div>
              </div>
              <div className="criteria-item-row">
                <span className="criteria-item-icon">👓</span>
                <div className="criteria-item-details">
                  <strong>Eyewear:</strong> At least 1 selfie without glasses (50% target)
                </div>
              </div>
              <div className="criteria-item-row">
                <span className="criteria-item-icon">🌍</span>
                <div className="criteria-item-details">
                  <strong>Background:</strong> Move to a different spot/background for each selfie
                </div>
              </div>
            </div>
          </div>

          <div className="mobile-examples-container" style={{ marginTop: '1rem' }}>
            <div className="examples-header-label">Examples of acceptable selfie captures:</div>
            <ExampleCarousel
              photos={examplePhotos.filter(p => {
                const cat = (p.category || '').toLowerCase();
                return ['present_female', 'present_girl', 'present_male'].includes(cat);
              })}
              categoryLabel="present"
              onSelectImage={setSelectedImage}
            />
          </div>

          {/* Upload Method Switcher */}
          <div className="method-selector">
            <button
              className={`method-btn ${presentUploadMethod === 'files' ? 'active' : ''}`}
              onClick={() => { setPresentUploadMethod('files'); setShowCamera(false); }}
            >
              📁 Multiple Files Upload
            </button>
            <button
              className={`method-btn ${presentUploadMethod === 'camera' ? 'active' : ''}`}
              onClick={() => { setPresentUploadMethod('camera'); }}
            >
              📷 Live Camera
            </button>
          </div>

          {/* Progress Bar */}
          <div className="photo-progress">
            <div className="progress-header">
              <span>Accepted Photos: <strong>{validPresentPhotos.length} / 6</strong></span>
              <span>Total Uploaded: {presentPhotos.length}</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${Math.min((validPresentPhotos.length / 6) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Upload Dropzone */}
          {presentUploadMethod === 'files' && (
            <div className="file-upload">
              <label
                className={`file-upload-label ${isDragging ? 'drag-active' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, handlePresentFilesUpload)}
              >
                <span className="upload-icon">📂</span>
                <span className="upload-text">Upload Present Photos (Single or Multiple)</span>
                <span className="upload-subtext">Select files from your device</span>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handlePresentFilesUpload}
                  disabled={loading}
                  className="file-input"
                />
              </label>
            </div>
          )}

          {/* Camera Section */}
          {presentUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              <LiveCaptureModal
                isOpen={showCamera && currentStep === 'present'}
                onClose={() => setShowCamera(false)}
                onCapture={async (file) => {
                  await uploadPresentPhotos([file]);
                }}
                stage="Present_Neutral"
                currentCount={validPresentPhotos.length}
                maxCount={5}
                title="Present Selfie Verification"
                description="Align your face straight into the oval. Hold still until the ring fills green."
              />
            </div>
          )}

          {/* Present Photos Inspection Grid */}
          {presentPhotos.length > 0 && (
            <div className="photo-inspection-section">
              <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <h4>Uploaded Present Photos ({presentPhotos.length})</h4>
                  <span className="badge-summary">
                    {validPresentPhotos.length} Accepted • {presentPhotos.length - validPresentPhotos.length} Rejected
                  </span>
                </div>
                <button
                  onClick={() => handleRemoveAllPhotos('present')}
                  style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Remove All
                </button>
              </div>

              <div className="cards-grid">
                {presentPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const isValid = photo.is_valid || val.is_valid;
                  const reasons = val.rejection_reasons || [];
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);

                  return (
                    <div key={photo.id || index} className={`photo-card ${isValid ? 'card-accepted' : 'card-rejected'}`}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Present upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                          onError={(e) => { e.target.onerror = null; e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23334155'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2394a3b8' font-family='sans-serif' font-size='14'%3EPresent Photo%3C/text%3E%3C/svg%3E"; }}
                        />
                        <div className={`status-pill ${isValid ? 'pill-success' : 'pill-danger'}`}>
                          {isValid ? '✅ Accepted' : '❌ Rejected'}
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Photo #{index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'present')}
                            className="delete-photo-btn"
                            title="Remove and replace image"
                          >
                            🗑️ Remove
                          </button>
                        </div>

                        {/* Checks list */}
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Front Facing:</span>
                            <span className={val.front_facing !== false ? 'text-ok' : 'text-bad'}>
                              {val.front_facing !== false ? '✓ Direct' : '✗ Angled'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Shoulders Visible:</span>
                            <span className={val.shoulders_visible !== false ? 'text-ok' : 'text-bad'}>
                              {val.shoulders_visible !== false ? '✓ Visible' : '✗ Obscured'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Eyewear:</span>
                            <span style={{ fontWeight: 600, color: val.has_glasses ? '#d97706' : '#16a34a' }}>
                              {val.has_glasses ? '👓 Glasses detected' : '👁️ No glasses'}
                            </span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>

                        {/* Rejection Reasons */}
                        {!isValid && (
                          <div className="rejection-box">
                            <span className="rejection-title">⚠️ Rejection Reason:</span>
                            {reasons.length > 0 ? (
                              <ul className="reasons-list">
                                {reasons.map((r, i) => <li key={i}>{r}</li>)}
                              </ul>
                            ) : (
                              <p className="single-reason">Please replace with a sharp, front-facing photo.</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Final Pre-Approval and Submission */}
              <div className="approval-action-bar">
                {presentPhotos.length >= 6 ? (
                  <div className="approval-box approved-state">
                    <div className="approval-text">
                      <strong>✅ 6 Photos Uploaded!</strong>
                      <p>You have uploaded {presentPhotos.length} present photo(s) ({validPresentPhotos.length} accepted). You may proceed to the next step.</p>
                    </div>
                  </div>
                ) : (
                  <div className="approval-box warning-state">
                    <div className="approval-text">
                      <strong>⏳ {6 - presentPhotos.length} More Photo(s) Required</strong>
                      <p>You have uploaded {presentPhotos.length} of 6 required present photos. Upload at least 6 photos to proceed — not all need to be accepted.</p>
                    </div>
                  </div>
                )}
                
                <div className="step-navigation" style={{ width: '100%', marginTop: '1rem', borderTop: 'none', paddingTop: 0 }}>
                  <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('old')}>
                    &#8592; Previous
                  </button>
                  {(!actualUserId || Number(actualUserId) % 2 !== 0) ? (
                    <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('post_a')} disabled={!canAccessPost}
                      title={!canAccessPost ? `Upload ${Math.max(0, 6 - presentPhotos.length)} more photo(s) to proceed` : 'Proceed to Variation A'}
                    >
                      Next Step &#8594;
                    </button>
                  ) : (
                    <button className="step-nav-btn btn-next" onClick={() => setCurrentStep('post_b')} disabled={!canAccessPost}
                      title={!canAccessPost ? `Upload ${Math.max(0, 6 - presentPhotos.length)} more photo(s) to proceed` : 'Proceed to Variation B'}
                    >
                      Next Step &#8594;
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing and validating image(s)...</p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* STEP 3: POST Variation A */}
            {/* STEP 2: POSES TAB (Variation A & Variation B) */}
      {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 2: Pose Variations (8 Poses Target)</h3>
            <p>Complete 4 directional poses (Variation A) and 4 diagonal band poses (Variation B).</p>
          </div>

          {/* Subtab Switcher: Variation A vs Variation B */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <button
              type="button"
              onClick={() => setActivePoseSubtab('a')}
              style={{
                padding: '0.65rem 1.5rem',
                borderRadius: '8px',
                border: activePoseSubtab === 'a' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                background: activePoseSubtab === 'a' ? '#3b82f6' : '#ffffff',
                color: activePoseSubtab === 'a' ? '#ffffff' : '#334155',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Variation A (Poses 1–4) • {postAPhotos.length}/4
            </button>
            <button
              type="button"
              onClick={() => setActivePoseSubtab('b')}
              style={{
                padding: '0.65rem 1.5rem',
                borderRadius: '8px',
                border: activePoseSubtab === 'b' ? '2px solid #3b82f6' : '1px solid #cbd5e1',
                background: activePoseSubtab === 'b' ? '#3b82f6' : '#ffffff',
                color: activePoseSubtab === 'b' ? '#ffffff' : '#334155',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Variation B (Diagonals 5–8) • {postBPhotos.length}/4
            </button>
          </div>
        </div>
      )}

      {(currentStep === 'post_a' || (currentStep === 'poses' && activePoseSubtab === 'a')) && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 3: Post Variation A</h3>
            <p>Upload any additional requested Variation images here. This section is optional.</p>
          </div>

          <div className="requirements">
            <p><strong>Instructions — 4 Required Poses:</strong></p>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              Take one photo per pose listed below. Look in the indicated direction while keeping your face fully in frame.
            </p>

            {/* Per-Pose Grid with embedded examples */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '1rem' }}>
              {[
                { key: 'up',    label: 'Pose 1 — UP',    arrow: '⬆️', desc: 'Tilt your chin upward, looking up toward the ceiling.' },
                { key: 'left',  label: 'Pose 2 — LEFT',  arrow: '⬅️', desc: 'Turn your head to the left side.' },
                { key: 'down',  label: 'Pose 3 — DOWN',  arrow: '⬇️', desc: 'Tilt your chin downward, looking toward the floor.' },
                { key: 'right', label: 'Pose 4 — RIGHT', arrow: '➡️', desc: 'Turn your head to the right side.' },
              ].map(pose => {
                const matchingPhoto = examplePhotos.find(p => {
                  const cat = (p.category || '').toLowerCase();
                  return cat.startsWith('posta') && cat.includes(pose.key);
                });
                return (
                  <div key={pose.key} style={{
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    {/* Example photo */}
                    <div style={{ position: 'relative', background: '#f8fafc', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {matchingPhoto ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`}
                          alt={`Example for ${pose.label}`}
                          style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`)}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem', color: '#94a3b8' }}>
                          <span style={{ fontSize: '2.5rem' }}>{pose.arrow}</span>
                          <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>No example uploaded</span>
                        </div>
                      )}
                      {/* Arrow badge overlay */}
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(15,23,42,0.75)', borderRadius: '8px', padding: '0.25rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ fontSize: '1rem' }}>{pose.arrow}</span>
                      </div>
                    </div>
                    {/* Pose info */}
                    <div style={{ padding: '0.85rem 1rem' }}>
                      <p style={{ margin: '0 0 0.35rem', fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>{pose.label}</p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4, marginBottom: '0.85rem' }}>{pose.desc}</p>
                      
                      <label className="pose-upload-btn-friendly" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', textAlign: 'center', padding: '0.85rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px -2px rgba(59, 130, 246, 0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.3)'; }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📸</span> Upload This Pose
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                          const file = e.target.files[0];
                          if(file) {
                             const fakeEvent = { target: { files: [file] } };
                             handlePostAFilesUpload(fakeEvent);
                          }
                          e.target.value = '';
                        }} disabled={loading} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="method-selector">
            <button
              className="method-btn active"
              onClick={() => { setPostAUploadMethod('camera'); }}
              style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            >
              📷 Live Camera Session
            </button>
          </div>

          {postAUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              <LiveCaptureModal
                isOpen={showCamera && (currentStep === 'post_a' || (currentStep === 'poses' && activePoseSubtab === 'a'))}
                onClose={() => setShowCamera(false)}
                onCapture={async (file) => {
                  await uploadPostAPhotos([file]);
                }}
                stage="Pose_Variation_A"
                poseNumber={postAPhotos.length + 1}
                currentCount={postAPhotos.length}
                maxCount={4}
                title="Pose Variation A"
                description="Follow the directional arrow and match the target angle."
              />
            </div>
          )}

          <div className="photo-inspection-section" style={{ marginTop: '2rem' }}>
            <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>Pose Conditions Analysis</h4>
              <button
                onClick={() => handleRemoveAllPhotos('post_a')}
                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove All Variation A
              </button>
            </div>

            <div className="uploaded-photos-flat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {postAPhotos.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '8px' }}>
                  No Post A photos uploaded yet.
                </div>
              ) : (
                postAPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                  return (
                    <div key={photo.id || index} className="photo-card card-accepted" style={{ border: '2px solid #e2e8f0' }}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Variation upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Image {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'post_a')}
                            className="delete-photo-btn"
                            title="Remove image"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Detected Pose:</span>
                            <span style={{ fontWeight: 'bold' }}>{val.detected_pose || 'UNASSIGNED'}</span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="step-navigation">
            <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('present')}>
              &#8592; Previous
            </button>
            <button className="step-nav-btn btn-next" onClick={goToDashboard}>
              Final Submit &#8594;
            </button>
          </div>
        </div>
      )}

      {/* STEP 4: POST Variation B */}
      {(currentStep === 'post_b' || (currentStep === 'poses' && activePoseSubtab === 'b')) && (
        <div className="upload-section">
          <div className="section-intro">
            <h3>Step 4: Post Variation B</h3>
            <p>Upload any additional Variation B images here. This section is optional.</p>
          </div>

          <div className="requirements">
            <p><strong>Instructions — 4 Required Diagonal Poses:</strong></p>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
              Take one photo per diagonal pose below. Move your gaze in the combined direction indicated.
            </p>

            {/* Per-Pose Grid with embedded examples */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '1rem' }}>
              {[
                { key: 'up_left',    label: 'Pose 5 — UP-LEFT',    arrow: '↖️', desc: 'Look up and to the left diagonally.' },
                { key: 'up_right',   label: 'Pose 6 — UP-RIGHT',   arrow: '↗️', desc: 'Look up and to the right diagonally.' },
                { key: 'down_left',  label: 'Pose 7 — DOWN-LEFT',  arrow: '↙️', desc: 'Look down and to the left diagonally.' },
                { key: 'down_right', label: 'Pose 8 — DOWN-RIGHT', arrow: '↘️', desc: 'Look down and to the right diagonally.' },
              ].map(pose => {
                const matchingPhoto = examplePhotos.find(p => {
                  const cat = (p.category || '').toLowerCase();
                  return cat.startsWith('postb') && cat.includes(pose.key);
                });
                return (
                  <div key={pose.key} style={{
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                    {/* Example photo */}
                    <div style={{ position: 'relative', background: '#f8fafc', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {matchingPhoto ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`}
                          alt={`Example for ${pose.label}`}
                          style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                          onClick={() => setSelectedImage(`${process.env.REACT_APP_API_URL || 'https://api.frinksyn.com/api'}/example-photos/${matchingPhoto.id}/file`)}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', padding: '1.5rem', color: '#94a3b8' }}>
                          <span style={{ fontSize: '2.5rem' }}>{pose.arrow}</span>
                          <span style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>No example uploaded</span>
                        </div>
                      )}
                      {/* Arrow badge overlay */}
                      <div style={{ position: 'absolute', top: '8px', left: '8px', background: 'rgba(15,23,42,0.75)', borderRadius: '8px', padding: '0.25rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <span style={{ fontSize: '1rem' }}>{pose.arrow}</span>
                      </div>
                    </div>
                    {/* Pose info */}
                    <div style={{ padding: '0.85rem 1rem' }}>
                      <p style={{ margin: '0 0 0.35rem', fontWeight: '700', fontSize: '0.95rem', color: '#0f172a' }}>{pose.label}</p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: '#64748b', lineHeight: 1.4, marginBottom: '0.85rem' }}>{pose.desc}</p>
                      
                      <label className="pose-upload-btn-friendly" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: 'white', textAlign: 'center', padding: '0.85rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)', transition: 'all 0.2s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 12px -2px rgba(59, 130, 246, 0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.3)'; }}
                      >
                        <span style={{ fontSize: '1.2rem' }}>📸</span> Upload This Pose
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                          const file = e.target.files[0];
                          if(file) {
                             const fakeEvent = { target: { files: [file] } };
                             handlePostBFilesUpload(fakeEvent);
                          }
                          e.target.value = '';
                        }} disabled={loading} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="method-selector">
            <button
              className="method-btn active"
              onClick={() => { setPostBUploadMethod('camera'); }}
              style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}
            >
              📷 Live Camera Session
            </button>
          </div>

          {postBUploadMethod === 'camera' && (
            <div className="camera-wrapper">
              {!showCamera && (
                <button onClick={() => setShowCamera(true)} className="start-camera-btn">
                  📷 Start Camera Session
                </button>
              )}

              <LiveCaptureModal
                isOpen={showCamera && (currentStep === 'post_b' || (currentStep === 'poses' && activePoseSubtab === 'b'))}
                onClose={() => setShowCamera(false)}
                onCapture={async (file) => {
                  await uploadPostBPhotos([file]);
                }}
                stage="Pose_Variation_B"
                poseNumber={postBPhotos.length + 5}
                currentCount={postBPhotos.length}
                maxCount={4}
                title="Pose Variation B (Diagonals)"
                description="Follow the diagonal target angle into the green zone."
              />
            </div>
          )}

          <div className="photo-inspection-section" style={{ marginTop: '2rem' }}>
            <div className="inspection-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4>Pose Conditions Analysis</h4>
              <button
                onClick={() => handleRemoveAllPhotos('post_b')}
                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Remove All Variation B
              </button>
            </div>

            <div className="uploaded-photos-flat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
              {postBPhotos.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#94a3b8', fontStyle: 'italic', background: '#f8fafc', borderRadius: '8px' }}>
                  No Post B photos uploaded yet.
                </div>
              ) : (
                postBPhotos.map((photo, index) => {
                  const val = photo.validation_results || {};
                  const photoUrl = apiService.getPhotoUrl(photo.preview_url || photo.url || photo.id);
                  return (
                    <div key={photo.id || index} className="photo-card card-accepted" style={{ border: '2px solid #e2e8f0' }}>
                      <div className="photo-preview-box">
                        <img
                          src={photoUrl}
                          alt={`Variation upload ${index + 1}`}
                          className="preview-img"
                          onClick={() => setSelectedImage(photoUrl)}
                        />
                      </div>
                      <div className="card-body">
                        <div className="card-title-row">
                          <h5>Image {index + 1}</h5>
                          <button
                            onClick={() => handleDeletePhoto(photo.id, 'post_b')}
                            className="delete-photo-btn"
                            title="Remove image"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                        <div className="checks-list">
                          <div className="check-row">
                            <span>Detected Pose:</span>
                            <span style={{ fontWeight: 'bold' }}>{val.detected_pose || 'UNASSIGNED'}</span>
                          </div>
                          <div className="check-row">
                            <span>Actual Date (EXIF):</span>
                            <span className={val.exif_date ? 'text-ok' : 'text-bad'}>
                              {val.exif_date ? <span>✓ {val.exif_date.split(' ')[0]}&nbsp;&nbsp;&nbsp;&nbsp;{val.exif_date.split(' ')[1] || ''}</span> : '✗ Not Found'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="step-navigation">
            <button className="step-nav-btn btn-prev" onClick={() => setCurrentStep('present')}>
              &#8592; Previous
            </button>
            <button className="step-nav-btn btn-next" onClick={goToDashboard}>
              Final Submit &#8594;
            </button>
          </div>
        </div>
      )}

      
      {/* STEP 4: REVIEW & HAND IN VIEW */}
      {currentStep === 'hand_in' && (
        <div className="upload-section mobile-review-section">
          <div className="section-intro">
            <h3>Step 4: Verification Package Review</h3>
            <p>Inspect all submitted photos and verify compliance before final submission.</p>
          </div>

          {/* Overall Status Banner */}
          <div className={`review-overall-banner ${isReadyForHandIn ? 'ready' : 'pending'}`}>
            <div className="banner-icon">{isReadyForHandIn ? '🎉' : '⏳'}</div>
            <div className="banner-content">
              <strong>{isReadyForHandIn ? 'All Requirements Completed!' : 'Verification Incomplete'}</strong>
              <p>
                {isReadyForHandIn
                  ? 'All criteria for Historical, Selfies, and Poses are satisfied. You may finalize and hand in.'
                  : 'Please complete all required photo sections below before handing in.'}
              </p>
            </div>
          </div>

          {/* Review Cards Grid */}
          <div className="review-cards-list">
            {/* Card 1: Historical */}
            <div className={`review-category-card ${isHistorySatisfied ? 'card-complete' : 'card-pending'}`}>
              <div className="review-card-header">
                <div className="category-title-box">
                  <span className="category-icon">⏳</span>
                  <div>
                    <h4>1. Historical Photos</h4>
                    <span className="category-meta">{validOldPhotos.length} accepted of {oldPhotos.length} total (Min 3 required)</span>
                  </div>
                </div>
                <div className="category-action">
                  <button type="button" onClick={() => setCurrentStep('old')} className="category-edit-btn">
                    {isHistorySatisfied ? '✓ Edit' : 'Add Photos →'}
                  </button>
                </div>
              </div>
              <div className="review-thumbs-row">
                {oldPhotos.slice(0, 4).map((p, i) => (
                  <img
                    key={p.id || i}
                    src={apiService.getPhotoUrl(p.preview_url || p.url || p.id)}
                    alt={`Historical ${i + 1}`}
                    className="review-thumb-img"
                    onClick={() => setSelectedImage(apiService.getPhotoUrl(p.preview_url || p.url || p.id))}
                  />
                ))}
                {oldPhotos.length > 4 && (
                  <div className="review-more-bubble">+{oldPhotos.length - 4}</div>
                )}
              </div>
            </div>

            {/* Card 2: Selfies */}
            <div className={`review-category-card ${isSelfiesSatisfied ? 'card-complete' : 'card-pending'}`}>
              <div className="review-card-header">
                <div className="category-title-box">
                  <span className="category-icon">🤳</span>
                  <div>
                    <h4>2. Live Selfies</h4>
                    <span className="category-meta">
                      {validPresentPhotos.length} verified (Min 2 required, target 5) • {validPresentPhotos.filter(p => !p.validation_results?.has_glasses).length} without glasses
                    </span>
                  </div>
                </div>
                <div className="category-action">
                  <button type="button" onClick={() => setCurrentStep('present')} className="category-edit-btn">
                    {isSelfiesSatisfied ? '✓ Edit' : 'Capture Selfies →'}
                  </button>
                </div>
              </div>
              <div className="review-thumbs-row">
                {presentPhotos.slice(0, 5).map((p, i) => (
                  <img
                    key={p.id || i}
                    src={apiService.getPhotoUrl(p.preview_url || p.url || p.id)}
                    alt={`Selfie ${i + 1}`}
                    className="review-thumb-img"
                    onClick={() => setSelectedImage(apiService.getPhotoUrl(p.preview_url || p.url || p.id))}
                  />
                ))}
              </div>
            </div>

            {/* Card 3: Poses */}
            <div className={`review-category-card ${isPosesSatisfied ? 'card-complete' : 'card-pending'}`}>
              <div className="review-card-header">
                <div className="category-title-box">
                  <span className="category-icon">🎯</span>
                  <div>
                    <h4>3. Head Poses (Variation A & B)</h4>
                    <span className="category-meta">
                      {postAPhotos.length} Directional (A) + {postBPhotos.length} Diagonal (B) (Target: 8)
                    </span>
                  </div>
                </div>
                <div className="category-action">
                  <button type="button" onClick={() => setCurrentStep('poses')} className="category-edit-btn">
                    {isPosesSatisfied ? '✓ Edit' : 'Complete Poses →'}
                  </button>
                </div>
              </div>
              <div className="review-thumbs-row">
                {[...postAPhotos, ...postBPhotos].slice(0, 6).map((p, i) => (
                  <img
                    key={p.id || i}
                    src={apiService.getPhotoUrl(p.preview_url || p.url || p.id)}
                    alt={`Pose ${i + 1}`}
                    className="review-thumb-img"
                    onClick={() => setSelectedImage(apiService.getPhotoUrl(p.preview_url || p.url || p.id))}
                  />
                ))}
                {(postAPhotos.length + postBPhotos.length) > 6 && (
                  <div className="review-more-bubble">+{(postAPhotos.length + postBPhotos.length) - 6}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      
      {/* Pinned Sticky Bottom Action Bar with Safe Area */}
      <div className="sticky-mobile-footer">
        <div className="sticky-footer-container">
          {currentStep === 'old' && (
            <div className="sticky-footer-inner">
              <div className="sticky-helper-text">
                {validOldPhotos.length >= 3 ? (
                  <span className="hint-success">✅ {validOldPhotos.length} historical photos accepted</span>
                ) : (
                  <span className="hint-pending">📁 Upload {Math.max(0, 3 - validOldPhotos.length)} more historical photo(s) to proceed</span>
                )}
              </div>
              <button
                type="button"
                className="sticky-action-btn primary-btn"
                onClick={() => setCurrentStep('present')}
                disabled={!canAccessPresent}
              >
                <span>Continue to Selfies</span>
                <span className="btn-chevron">→</span>
              </button>
            </div>
          )}

          {currentStep === 'present' && (
            <div className="sticky-footer-inner">
              <div className="sticky-helper-text">
                {validPresentPhotos.length >= 2 ? (
                  <span className="hint-success">✅ {validPresentPhotos.length} selfies verified ({validPresentPhotos.length}/5)</span>
                ) : (
                  <span className="hint-pending">🤳 Capture at least 2 accepted front selfies to proceed</span>
                )}
              </div>
              <div className="sticky-dual-btn-group">
                <button
                  type="button"
                  className="sticky-action-btn secondary-btn"
                  onClick={() => setCurrentStep('old')}
                >
                  ← Historical
                </button>
                <button
                  type="button"
                  className="sticky-action-btn primary-btn"
                  onClick={() => setCurrentStep('poses')}
                  disabled={!canAccessPost}
                >
                  <span>Continue to Poses</span>
                  <span className="btn-chevron">→</span>
                </button>
              </div>
            </div>
          )}

          {(currentStep === 'poses' || currentStep === 'post_a' || currentStep === 'post_b') && (
            <div className="sticky-footer-inner">
              <div className="sticky-helper-text">
                {isPosesSatisfied ? (
                  <span className="hint-success">✅ Poses verified ({postAPhotos.length + postBPhotos.length}/8)</span>
                ) : (
                  <span className="hint-pending">🎯 Capture poses for Variation A & B ({postAPhotos.length + postBPhotos.length}/8)</span>
                )}
              </div>
              <div className="sticky-dual-btn-group">
                <button
                  type="button"
                  className="sticky-action-btn secondary-btn"
                  onClick={() => setCurrentStep('present')}
                >
                  ← Selfies
                </button>
                <button
                  type="button"
                  className="sticky-action-btn primary-btn"
                  onClick={() => setCurrentStep('hand_in')}
                >
                  <span>Review Package</span>
                  <span className="btn-chevron">→</span>
                </button>
              </div>
            </div>
          )}

          {currentStep === 'hand_in' && (
            <div className="sticky-footer-inner">
              <div className="sticky-helper-text">
                {isReadyForHandIn ? (
                  <span className="hint-success">🎉 All verification requirements satisfied!</span>
                ) : (
                  <span className="hint-warning">⚠️ Complete remaining required items above to submit</span>
                )}
              </div>
              <div className="sticky-dual-btn-group">
                <button
                  type="button"
                  className="sticky-action-btn secondary-btn"
                  onClick={() => setCurrentStep('poses')}
                >
                  ← Poses
                </button>
                <button
                  type="button"
                  className="sticky-action-btn primary-btn submit-btn"
                  onClick={goToDashboard}
                  disabled={!isReadyForHandIn}
                >
                  <span>Finalize & Submit</span>
                  <span className="btn-chevron">✓</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedImage(null)}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>&times;</button>
            <img src={selectedImage} alt="Expanded view" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
